import React, { useState } from 'react';
import './BalloonRelease.css';

interface Balloon {
    id: number;
    x: number;
    color: string;
    delay: number;
    avoidanceOffset: number;
}

const BalloonRelease: React.FC = () => {
    const [balloons, setBalloons] = useState<Balloon[]>([]);
    const [balloonCount, setBalloonCount] = useState(0);

    // Cloud positions (approximate percentages of screen width)
    const cloudZones = [
        { start: 10, end: 25, height: 10 }, // Cloud 1
        { start: 40, end: 60, height: 25 }, // Cloud 2
        { start: 70, end: 85, height: 15 }, // Cloud 3
    ];

    const balloonColors = [
        '#FF6B6B', // Red
        '#4ECDC4', // Teal
        '#45B7D1', // Blue
        '#96CEB4', // Green
        '#FFEAA7', // Yellow
        '#DDA0DD', // Plum
        '#FFB6C1', // Light Pink
        '#98D8C8', // Mint
        '#F7DC6F', // Light Yellow
        '#BB8FCE', // Light Purple
    ];

    const isInCloudZone = (x: number) => {
        const xPercent = (x / window.innerWidth) * 100;
        return cloudZones.some((cloud) => xPercent >= cloud.start && xPercent <= cloud.end);
    };

    const generateSafePosition = () => {
        let attempts = 0;
        let x;
        do {
            x = Math.random() * (window.innerWidth - 100) + 50;
            attempts += 1;
        } while (isInCloudZone(x) && attempts < 10);
        return x;
    };

    const releaseBalloon = () => {
        const safeX = generateSafePosition();
        let avoidanceOffset = 0;
        if (isInCloudZone(safeX)) {
            avoidanceOffset = Math.random() > 0.5 ? 30 : -30;
        }

        const newBalloon: Balloon = {
            id: balloonCount,
            x: safeX,
            color: balloonColors[Math.floor(Math.random() * balloonColors.length)],
            delay: Math.random() * 0.5, // Random delay up to 0.5 seconds
            avoidanceOffset,
        };

        setBalloons((prev) => [...prev, newBalloon]);
        setBalloonCount((prev) => prev + 1);

        // Remove balloon after animation completes (now 15 seconds)
        setTimeout(() => {
            setBalloons((prev) => prev.filter((balloon) => balloon.id !== newBalloon.id));
        }, 15000);
    };

    const releaseBatch = () => {
        const batchSize = 5 + Math.floor(Math.random() * 5); // 5-10 balloons
        for (let i = 0; i < batchSize; i += 1) {
            setTimeout(() => releaseBalloon(), i * 200); // Stagger releases
        }
    };

    const clearAllBalloons = () => {
        setBalloons([]);
    };

    return (
        <div className="balloon-release-container balloon-cursor">
            <div className="balloon-controls">
                <h1>🎈 Balloon Release 🎈</h1>
                <p>Release beautiful balloons and watch them float up to the sky!</p>

                <div className="button-group">
                    <button type="button" className="release-btn single" onClick={releaseBalloon}>
                        Release Single Balloon
                    </button>
                    <button type="button" className="release-btn batch" onClick={releaseBatch}>
                        Release Balloon Batch
                    </button>
                    <button type="button" className="release-btn clear" onClick={clearAllBalloons}>
                        Clear All Balloons
                    </button>
                </div>

                <div className="stats">
                    <p>Active Balloons: {balloons.length}</p>
                    <p>Total Released: {balloonCount}</p>
                </div>
            </div>

            {/* Balloon Animation Area */}
            <div className="balloon-area">
                {balloons.map((balloon) => (
                    <div
                        key={balloon.id}
                        className="balloon balloon-floating"
                        style={{
                            left: balloon.x + balloon.avoidanceOffset,
                            top: 'calc(100vh - 80px)', // Start at bottom of screen
                            background: `linear-gradient(
                                135deg,
                                ${balloon.color},
                                ${balloon.color}dd
                            )`,
                            animationDelay: `${balloon.delay}s`,
                        }}
                    >
                        <div className="balloon-body" />
                        <div className="balloon-string" />
                        <div className="balloon-highlight" />
                    </div>
                ))}
            </div>

            {/* Floating Clouds Background */}
            <div className="clouds">
                {['cloud1', 'cloud2', 'cloud3'].map((cloudId, i) => (
                    <div key={cloudId} className={`cloud cloud-${i + 1}`}>
                        ☁️
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BalloonRelease;
